import React, { useCallback, useEffect, useState } from "react";
import "../inward-styles.css";
import { MobileCard, MobileField } from "../common-components/MobileCard";
import { Redirect } from "react-router-dom";
import { useAuth } from "../../../../context_providers/auth/authProvider";
import { fetchGet } from "../../../../service/api_client";
import { ACTION } from "../../../../utils/enums";
import { urls } from "../../../../utils/urls";

const modules = [
  "All",
  "Inward",
  "Outward",
  "Warehouse",
  "Stock Transfer",
  "Finance",
  "Faulty Resolution",
];

type WarehouseLogUser =
  | string
  | {
      user_id?: string;
      user_name?: string;
    }
  | null
  | undefined;

type JsonLike =
  | string
  | number
  | boolean
  | null
  | undefined
  | JsonLike[]
  | { [key: string]: JsonLike };

interface WarehouseLog {
  _id?: string;
  date?: string;
  action?: string;
  sub_action?: string;
  module?: string;
  user?: WarehouseLogUser;
  status?: string;
  remarks?: string;
  prevState?: Record<string, JsonLike> | null;
  currState?: Record<string, JsonLike> | null;
  changed_fields?: Record<string, JsonLike> | null;
  added_fields?: Record<string, JsonLike> | null;
  deleted_fields?: Record<string, JsonLike> | null;
}

export const WarehouseLogs = () => {
  const { hasRoleTitle } = useAuth();
  const [logs, setLogs] = useState<WarehouseLog[]>([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  const [canViewWarehouseLogs, setCanViewWarehouseLogs] =
    useState<boolean>(true);

  const [filters, setFilters] = useState({
    from: "",
    to: "",
    module: "All",
    search: "",
  });

  interface LogsResponse {
    data: any[];
    totalPages: number;
  }

  const getAllWarehouseLogs = async (
    page: number,
    filters: any,
  ): Promise<LogsResponse> => {
    const query = new URLSearchParams({
      page: page.toString(),
      from: filters.from,
      to: filters.to,
      module: filters.module,
      search: filters.search?.trim() || "",
    });

    const res = await fetchGet(
      `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/warehouse-logs/get-logs?${query.toString()}`,
    );

    if (!res?.success) {
      throw new Error("Failed to fetch logs");
    }

    return {
      data: res.data,
      totalPages: res.totalPages,
    };
  };

  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    return d.toLocaleString();
  };

  const renderUser = (user: WarehouseLogUser) => {
    if (!user) return "-";
    if (typeof user === "string") return user;
    return user.user_name || user.user_id || "-";
  };

  const isNonEmptyObject = (val: unknown): val is Record<string, unknown> => {
    if (!val || typeof val !== "object" || Array.isArray(val)) return false;
    return Object.keys(val as Record<string, unknown>).length > 0;
  };

  const formatJson = (val: JsonLike) => {
    if (val === null || val === undefined) return "-";
    if (typeof val === "string") return val || "-";
    if (typeof val === "number" || typeof val === "boolean") return String(val);
    try {
      return JSON.stringify(val, null, 2);
    } catch {
      return String(val);
    }
  };

  const isArrayOfObjects = (
    arr: unknown[],
  ): arr is Record<string, JsonLike>[] =>
    arr.length > 0 &&
    arr.every(
      (item) =>
        item !== null &&
        typeof item === "object" &&
        !Array.isArray(item) &&
        isNonEmptyObject(item),
    );

  const renderArrayAsTable = (arr: Record<string, JsonLike>[]) => {
    if (arr.length === 0)
      return <span className="text-gray-500 text-xs">No items</span>;
    const allKeys = Array.from(
      new Set(
        arr.flatMap((obj) => Object.keys(obj as Record<string, unknown>)),
      ),
    );
    return (
      <div className="overflow-auto">
        <table className="w-full text-xs border border-gray-200 rounded-md">
          <thead className="bg-gray-50">
            <tr>
              {allKeys.map((k) => (
                <th
                  key={k}
                  className="px-3 py-2 text-left font-medium text-gray-700 capitalize"
                >
                  {k.replace(/_/g, " ")}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {arr.map((item, idx) => (
              <tr key={idx}>
                {allKeys.map((k) => (
                  <td key={k} className="px-3 py-2 text-gray-800">
                    {formatJson((item as Record<string, JsonLike>)[k])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderValue = (v: JsonLike): React.ReactNode => {
    if (v === null || v === undefined)
      return <span className="text-gray-500">-</span>;
    if (
      typeof v === "string" ||
      typeof v === "number" ||
      typeof v === "boolean"
    ) {
      return <span className="text-gray-800 break-words">{String(v)}</span>;
    }
    if (Array.isArray(v)) {
      if (v.length === 0)
        return <span className="text-gray-500 text-sm">No items</span>;
      if (isArrayOfObjects(v)) return renderArrayAsTable(v);
      return (
        <pre className="text-xs bg-gray-50 border border-gray-100 rounded-md p-2 whitespace-pre-wrap break-words">
          {formatJson(v)}
        </pre>
      );
    }
    if (isNonEmptyObject(v)) {
      return renderKeyValueTable(v as Record<string, JsonLike>);
    }
    return <span className="text-gray-800 break-words">{String(v)}</span>;
  };

  const renderKeyValueTable = (obj?: Record<string, JsonLike> | null) => {
    if (!isNonEmptyObject(obj))
      return <div className="text-xs text-gray-500">-</div>;
    const entries = Object.entries(obj as Record<string, JsonLike>);
    return (
      <div className="overflow-auto">
        <table className="w-full text-xs">
          <tbody className="divide-y divide-gray-100">
            {entries.map(([k, v]) => (
              <tr key={k} className="align-top">
                <td className="py-2 pr-4 w-48">
                  <span className="font-medium text-gray-700 break-words">
                    {k}
                  </span>
                </td>
                <td className="py-2">{renderValue(v)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const hasDetails = (log: WarehouseLog) =>
    isNonEmptyObject(log.prevState) ||
    isNonEmptyObject(log.currState) ||
    isNonEmptyObject(log.changed_fields) ||
    isNonEmptyObject(log.added_fields) ||
    isNonEmptyObject(log.deleted_fields);

  const isSpocAssignLog = (log: WarehouseLog) => {
    const isSpocAssignedToWarehouse =
      log.module === "Warehouse" &&
      log.sub_action === "assign-spoc" &&
      Array.isArray(log.added_fields);
    const isWarehouseAssignedToSpoc =
      log.module === "Warehouse" &&
      log.sub_action === "assign-spoc" &&
      typeof log.added_fields === "object" &&
      !Array.isArray(log.added_fields) &&
      Array.isArray((log.added_fields as any)?.warehouses);
    return isSpocAssignedToWarehouse || isWarehouseAssignedToSpoc;
  };

  const computeShowDetails = (log: WarehouseLog) =>
    log.module === "Warehouse" ? isSpocAssignLog(log) : hasDetails(log);

  const renderLogDetails = (log: WarehouseLog) => (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {log.module !== "Warehouse" && (
        <>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-xs font-semibold text-gray-600 uppercase mb-3">
              Prev State
            </div>
            {renderKeyValueTable(log.prevState)}
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-xs font-semibold text-gray-600 uppercase mb-3">
              Current State
            </div>
            {renderKeyValueTable(log.currState)}
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-xs font-semibold text-gray-600 uppercase mb-3">
              Changed Fields
            </div>
            {renderKeyValueTable(log.changed_fields)}
          </div>
        </>
      )}

      {log.module === "Warehouse" &&
        log.sub_action === "assign-spoc" &&
        isSpocAssignLog(log) && (
          <>
            <div className="bg-white border border-green-200 rounded-lg p-4 mb-4">
              <div className="text-xs font-semibold text-green-600 uppercase mb-3">
                Added Spocs
              </div>

              {Array.isArray(log.added_fields) &&
              log.added_fields.length > 0 ? (
                log.added_fields.map((item: any, index: number) => (
                  <div
                    key={index}
                    className="border border-green-100 p-3 rounded mb-2"
                  >
                    {Object.entries(item).map(([key, value]) => (
                      <p key={key} className="text-xs">
                        <strong className="capitalize">
                          {key.replace(/_/g, " ")}:
                        </strong>{" "}
                        {value ? String(value) : "-"}
                      </p>
                    ))}
                  </div>
                ))
              ) : (
                <p className="text-xs text-gray-500">
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    {renderKeyValueTable(log.added_fields)}
                  </div>
                </p>
              )}
            </div>

            <div className="bg-white border border-green-200 rounded-lg p-4 mb-4">
              <div className="text-xs font-semibold text-green-600 uppercase mb-3">
                Removed Spocs
              </div>

              {Array.isArray(log.deleted_fields) &&
              log.deleted_fields.length > 0 ? (
                log.deleted_fields.map((item: any, index: number) => (
                  <div
                    key={index}
                    className="border border-green-100 p-3 rounded mb-2"
                  >
                    {Object.entries(item).map(([key, value]) => (
                      <p key={key} className="text-xs">
                        <strong className="capitalize">
                          {key.replace(/_/g, " ")}:
                        </strong>{" "}
                        {value ? String(value) : "-"}
                      </p>
                    ))}
                  </div>
                ))
              ) : (
                <p className="text-xs text-gray-500">
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    {renderKeyValueTable(log.deleted_fields)}
                  </div>
                </p>
              )}
            </div>
          </>
        )}
    </div>
  );

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAllWarehouseLogs(page, filters);
      setLogs(Array.isArray(res.data) ? res.data : []);
      setTotalPages(res.totalPages || 1);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const onFilterChange = (e: any) => {
    setPage(1);
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const clearFilter = () => {
    setFilters({
      from: "",
      to: "",
      module: "All",
      search: "",
    });
    setPage(1);
  };

  useEffect(() => {
    const canViewWarehouseLogs = hasRoleTitle({
      roleTitle: "warehouse module admin",
      action: ACTION.CREATE,
    });

    setCanViewWarehouseLogs(canViewWarehouseLogs);
  }, []);

  if (!canViewWarehouseLogs) {
    return <Redirect to={urls.notAllowed} />;
  }

  return (
    <div
      id="warehouse-logs-screen"
      className="wh-mobile-shrink min-h-screen bg-gray-50 p-6"
    >
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Warehouse Logs</h1>

      {/* Filters */}
      <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200 mb-6 grid grid-cols-1 md:grid-cols-5 gap-4 items-start">
        <input
          id="warehouse-logs-filter-from-date"
          type="date"
          name="from"
          value={filters.from}
          onChange={onFilterChange}
          className="input"
        />
        <input
          id="warehouse-logs-filter-to-date"
          type="date"
          name="to"
          value={filters.to}
          onChange={onFilterChange}
          className="input"
        />

        <select
          id="warehouse-logs-filter-module"
          name="module"
          value={filters.module}
          onChange={onFilterChange}
          className="input"
        >
          {modules.map((m) => (
            <option key={m}>{m}</option>
          ))}
        </select>

        <input
          id="warehouse-logs-filter-search"
          name="search"
          value={filters.search}
          placeholder="Search logs..."
          onChange={onFilterChange}
          className="input"
        />

        <button
          id="warehouse-logs-clear-filters-btn"
          onClick={clearFilter}
          className="btn-clear"
        >
          Clear
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 text-xs text-gray-600 uppercase">
              <tr>
                <th className="px-5 py-3 text-left">Date</th>
                <th className="px-5 py-3 text-left">Module</th>
                <th className="px-5 py-3 text-left">Action</th>
                <th className="px-5 py-3 text-left">Sub Action</th>
                <th className="px-5 py-3 text-left">User</th>
                <th className="px-5 py-3 text-left">Remarks</th>
                <th className="px-5 py-3 text-right">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td colSpan={7} className="p-6 text-center">
                    Loading...
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="p-6 text-center text-xs text-gray-500"
                  >
                    No logs found.
                  </td>
                </tr>
              ) : (
                logs.map((log, i) => {
                  const id = log._id || String(i);
                  const expanded = expandedLogId === id;
                  const showDetails = computeShowDetails(log);

                  return (
                    <React.Fragment key={id}>
                      <tr
                        id={`warehouse-logs-row-${id}`}
                        className="hover:bg-gray-50 text-xs"
                      >
                        <td className="px-5 py-4">
                          {formatDateTime(log.date)}
                        </td>
                        <td className="px-5 py-4">{log.module || "-"}</td>
                        <td className="px-5 py-4">{log.action || "-"}</td>
                        <td className="px-5 py-4">{log.sub_action || "-"}</td>
                        <td className="px-5 py-4">{renderUser(log.user)}</td>
                        <td className="px-5 py-4">{log.remarks || "-"}</td>
                        <td className="px-5 py-4 text-right">
                          {showDetails ? (
                            <button
                              id={`warehouse-logs-toggle-details-${id}`}
                              className="btn"
                              onClick={() =>
                                setExpandedLogId((curr) =>
                                  curr === id ? null : id,
                                )
                              }
                            >
                              {expanded ? "Hide" : "View"}
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </td>
                      </tr>

                      {expanded && showDetails ? (
                        <tr className="bg-gray-50/40">
                          <td colSpan={7} className="px-5 py-4">
                            {renderLogDetails(log)}
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="md:hidden p-4 space-y-3">
          {loading ? (
            <div className="p-6 text-center text-xs">Loading...</div>
          ) : logs.length === 0 ? (
            <div className="p-6 text-center text-xs text-gray-500">
              No logs found.
            </div>
          ) : (
            logs.map((log, i) => {
              const id = log._id || String(i);
              const expanded = expandedLogId === id;
              const showDetails = computeShowDetails(log);

              return (
                <MobileCard key={id} id={`warehouse-logs-card-${id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-gray-900 break-words">
                        {log.action || "-"}
                      </p>
                      <p className="text-xs text-gray-500 break-words">
                        {formatDateTime(log.date)}
                      </p>
                    </div>
                    {log.module && (
                      <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 shrink-0">
                        {log.module}
                      </span>
                    )}
                  </div>
                  <div className="divide-y divide-gray-100">
                    <MobileField label="Sub Action">
                      {log.sub_action || "-"}
                    </MobileField>
                    <MobileField label="User">
                      {renderUser(log.user)}
                    </MobileField>
                    <MobileField label="Remarks">
                      {log.remarks || "-"}
                    </MobileField>
                  </div>
                  {showDetails && (
                    <button
                      id={`warehouse-logs-toggle-details-mobile-${id}`}
                      className="mt-2 inline-flex items-center gap-1 text-xs text-blue-600 font-medium"
                      onClick={() =>
                        setExpandedLogId((curr) => (curr === id ? null : id))
                      }
                    >
                      {expanded ? "Hide details" : "View details"}
                    </button>
                  )}
                  {expanded && showDetails && (
                    <div className="mt-2 text-xs">{renderLogDetails(log)}</div>
                  )}
                </MobileCard>
              );
            })
          )}
        </div>

        {/* Pagination */}
        <div
          id="warehouse-logs-pagination"
          className="p-4 flex justify-between items-center border-t text-xs"
        >
          <span>
            Page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              id="warehouse-logs-pagination-prev"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              className="btn"
            >
              Prev
            </button>
            <button
              id="warehouse-logs-pagination-next"
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="btn"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Styles */}
      <style>{`
  .input {
    border: 1px solid #e5e7eb;
    padding: 10px 12px;
    border-radius: 6px;
    font-size: 14px;
    color: #000;
  }

  .input::placeholder {
    color: #000;
    opacity: 1; /* important for Safari/Chrome */
  }

  .btn {
    border: 1px solid #e5e7eb;
    padding: 6px 14px;
    border-radius: 6px;
    background: white;
  }

  .btn:disabled {
    opacity: 0.4;
  }

  .btn-clear {
    border: 1px solid #ef4444;
    padding: 10px 14px;
    border-radius: 6px;
    background: #ef4444;
    color: white;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s;
  }

  .btn-clear:hover {
    background: #dc2626;
    border-color: #dc2626;
  }

  @media (max-width: 767px) {
    .input,
    .btn,
    .btn-clear {
      font-size: 12px;
    }
  }
`}</style>
    </div>
  );
};
